import { Modal, theme, Upload } from 'antd';
import { UploadFile, UploadProps } from 'antd/lib/upload/interface';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import grispiApi from '../../../apis/grispiApi';
import {
    grispiSubscriptionModal,
    subscriptionAttachmentLimitMap
} from '../../../containers/subscription/SubscriptionController';
import useUploadRequestAbort from '../../../hooks/useUploadRequestAbort';
import { UploadFilesResponse } from '../../../services/upload/uploadFiles';
import { useTicketFormUtilStore } from '../../../store/RootStoreContext';
import { grispiNotification } from '../../../store/useNotificationStore';
import { getCurrentUserRole, isAdminOrAgent, isEndUser } from '../../../utils/accountHelper';
import { generateTicketFormName } from '../../../utils/forms/formHelpers';
import CustomDivider from '../../CustomDivider';

enum FileUploadStatusMap {
    error = 'error',
    success = 'success',
    done = 'done',
    uploading = 'uploading',
    removed = 'removed'
}
export interface UploadProgressEvent extends ProgressEvent {
    percent: number;
}

export interface GrispiFile extends UploadFile {
    objectUrl?: string;
    id?: number;
    itemKeyOnSessionStorage?: string;
}

export interface UploadControllerProps {
    attachmentList: GrispiFile[];
    setAttachmentList: (attachmentList: GrispiFile[]) => void;
    onUploadSuccess: (file?: GrispiFile, inlineImage?: boolean) => void;
    onUploadError: () => void;
    internalView: boolean;
}

export const DEFAULT_SIZE_LIMIT = 51;
export const ADMIN_OR_AGENT_SIZE_LIMIT = DEFAULT_SIZE_LIMIT;
export const END_USER_SIZE_LIMIT = 51;

export const checkFileSizeLimit = (file: File, errorFlag = true) => {
    const fileSize = file.size / 1024 / 1024;

    const fileSizeLimit = isEndUser(getCurrentUserRole())
        ? END_USER_SIZE_LIMIT
        : isAdminOrAgent(getCurrentUserRole())
        ? ADMIN_OR_AGENT_SIZE_LIMIT
        : DEFAULT_SIZE_LIMIT;

    const isValid = fileSize <= fileSizeLimit;

    if (!isValid)
        grispiNotification(
            'error',
            getI18n().t('attachment_big', { maxSize: fileSizeLimit, ns: 'forms' }) as string,
            'error'
        );
    if (!isValid && errorFlag) throw new Error(`File size limit exceeded: ${fileSize} MB`);

    return isValid;
};

const UploadController = ({
    attachmentList,
    setAttachmentList,
    onUploadSuccess,
    internalView
}: UploadControllerProps) => {
    const {
        token: { borderRadius, gold1, paddingXXS }
    } = theme.useToken();
    const [modal, contextHolder] = Modal.useModal();

    const { t } = useTranslation('forms');
    const { asPath, pathname, query, events, push } = useRouter();
    const { setIsUploadingAttachment, resetFileListTriggered, resetFileList } = useTicketFormUtilStore();

    const [fileList, setFileList] = useState([]);
    const fileListLength = fileList.length;
    const isFormSubmitted = useRef(false);

    const source = useUploadRequestAbort();
    const cancelToken = source?.[asPath]?.token;
    const cancelUpload = useCallback(() => source.cancel(asPath), [asPath, source]);

    useEffect(() => {
        !isFormSubmitted?.current && fileListLength === 0 && attachmentList && setFileList([...attachmentList]);
    }, [attachmentList, fileListLength]);
    useEffect(() => {
        if (resetFileListTriggered) {
            setFileList([]);
            resetFileList(false);
            isFormSubmitted.current = true;
        }
    }, [resetFileList, resetFileListTriggered, setAttachmentList]);

    const showConfirmModal = useCallback(
        (url: string) => {
            modal.confirm({
                content: (
                    <div>
                        <p>{t('attachmentOngoingTitle')}</p>
                        <p>{t('attachmentOngoingDescription')}</p>
                    </div>
                ),
                onOk() {
                    cancelUpload();
                    const newAttachmentList = (attachmentList ?? []).filter((att) => att.status !== 'uploading');
                    setAttachmentList(newAttachmentList);
                    push(url);
                },
                okText: t('changeTab'),
                cancelText: t('stayOnTab')
            });
        },
        [attachmentList, cancelUpload, push, setAttachmentList, t, modal]
    );

    const handleRouteChangeStart = useCallback(
        (url: string) => {
            const hasUnfinishedUpload = attachmentList.find((att) => att.status === 'uploading' && att.percent < 100);

            if (hasUnfinishedUpload) {
                events.emit('routeChangeError');
                showConfirmModal(url);
                throw 'Abort route change. Please ignore this error.';
            }
        },
        [attachmentList, events, showConfirmModal]
    );

    useEffect(() => {
        events.on('routeChangeStart', handleRouteChangeStart);
        return () => {
            events.off('routeChangeStart', handleRouteChangeStart);
        };
    }, [events, handleRouteChangeStart]);

    const handleOnChange = ({ file, fileList }: { file: GrispiFile; fileList: GrispiFile[] }) => {
        if (file.status === FileUploadStatusMap.uploading) setIsUploadingAttachment(true);

        if (file.status === FileUploadStatusMap.done) {
            file.id = file.response.id;
            onUploadSuccess(file);
            grispiNotification('success', `${file.name} file attached successfully`);
            setIsUploadingAttachment(false);
        } else if (file.status === FileUploadStatusMap.removed) {
            cancelUpload();
            setAttachmentList([...fileList]);
        } else if (file.status === FileUploadStatusMap.error) {
            const error = `${file.name} file upload failed, try again`;
            grispiNotification('error', error);
            setIsUploadingAttachment(false);
            const newFileList = fileList.filter((file) => {
                return file.id !== file.id;
            });
            return setFileList([...newFileList]);
        }

        setFileList([...fileList]);
    };

    const handleBeforeUpload: UploadProps['beforeUpload'] = (file, fileList) => {
        const isValidSize = checkFileSizeLimit(file, false);
        if (!isValidSize) {
            fileList.pop();
            // If we don't return Upload.LIST_IGNORE file going look like its uploaded
            return false || Upload.LIST_IGNORE;
        }
    };

    const uploadImage: UploadProps<GrispiFile>['customRequest'] = async (options) => {
        const { onSuccess, onError, file, onProgress } = options;

        const fmData = new FormData();
        const config: AxiosRequestConfig = {
            headers: { 'content-type': 'multipart/form-data' },
            onUploadProgress: (event) => {
                onProgress({ percent: (event.loaded / event.total) * 100 } as UploadProgressEvent);
            },
            cancelToken
        };
        fmData.append('files', file);

        try {
            const res: AxiosResponse<UploadFilesResponse[]> = await grispiApi.post(
                '/attachments/upload',
                fmData,
                config
            );

            onSuccess(res.data[0], res.request);
        } catch (err: any) {
            const limitKey = err?.response?.data?.limitKey;
            const limit = Object.keys(subscriptionAttachmentLimitMap).find(
                (limit) => typeof limitKey === 'string' && limitKey.includes(limit)
            );
            if (limit) {
                grispiNotification('destroy', '', 'error');
                grispiSubscriptionModal({ limit: subscriptionAttachmentLimitMap[limit] });
            }
            onError(err, err.response?.data?.message);
        }
    };

    const elementId = `${generateTicketFormName(pathname, query)}-attachments`;

    const hasAttachment = fileList?.length > 0;

    return (
        <div
            id={elementId}
            style={{
                backgroundColor: internalView && hasAttachment ? gold1 : 'transparent',
                borderRadius,
                paddingBottom: hasAttachment ? paddingXXS : 0,
                paddingLeft: hasAttachment ? paddingXXS : 0,
                paddingRight: hasAttachment ? paddingXXS : 0,
                paddingTop: 0
            }}
        >
            {hasAttachment && <CustomDivider zeroMargin />}
            <Upload<GrispiFile>
                style={{ borderRadius }}
                beforeUpload={handleBeforeUpload}
                customRequest={uploadImage}
                fileList={fileList}
                listType="text"
                onChange={handleOnChange}
            />
            {contextHolder}
        </div>
    );
};

export default observer(UploadController);
